// 30-step intake spec (runbook v2 — May 2026).
// All steps are REQUIRED unless explicitly marked optional in the rendering layer.

import { getIntakeContent } from './intakeContent';

// Helper to translate step content based on language
export function translateStep(step, lang) {
  const ic = getIntakeContent(lang);

  // Map step IDs to translation keys
  const stepTranslations = {
    's04_gender': {
      title: ic.steps.gender.title,
      subtitle: ic.steps.gender.subtitle,
      options: [
        {v:"Male", l: ic.steps.gender.male},
        {v:"Female", l: ic.steps.gender.female},
        {v:"Prefer not to say", l: ic.steps.gender.preferNotToSay}
      ]
    },
    's06_motivation': {
      title: ic.steps.motivation.title,
      subtitle: ic.steps.motivation.subtitle,
      options: [
        {v:"confident", l: ic.steps.motivation.confident},
        {v:"energy", l: ic.steps.motivation.energy},
        {v:"health", l: ic.steps.motivation.health},
        {v:"active", l: ic.steps.motivation.active},
        {v:"feel_good", l: ic.steps.motivation.feelGood},
      ]
    },
    's07_goal': {
      title: ic.steps.weightLossGoal.title,
      options: [
        {v:"1_15", l: ic.steps.weightLossGoal["1_15"]},
        {v:"16_50", l: ic.steps.weightLossGoal["16_50"]},
        {v:"51_plus", l: ic.steps.weightLossGoal["51_plus"]},
        {v:"unsure", l: ic.steps.weightLossGoal.unsure},
      ]
    },
    's11_attempts': {
      title: ic.steps.weightLossAttempts.title,
      subtitle: ic.steps.weightLossAttempts.subtitle,
      options: [
        {v:"counting_calories", l: ic.steps.weightLossAttempts.countingCalories},
        {v:"commercial_programs", l: ic.steps.weightLossAttempts.commercialPrograms},
        {v:"low_carb_keto", l: ic.steps.weightLossAttempts.lowCarbKeto},
        {v:"intermittent_fasting", l: ic.steps.weightLossAttempts.intermittentFasting},
        {v:"exercise", l: ic.steps.weightLossAttempts.exercise},
        {v:"other", l: ic.common.other},
      ]
    },
    's12_bariatric': {
      title: ic.steps.bariatric.title,
      options: [
        {v:"lap_band", l: ic.steps.bariatric.lapBand},
        {v:"gastric_sleeve", l: ic.steps.bariatric.gastricSleeve},
        {v:"gastric_bypass", l: ic.steps.bariatric.gastricBypass},
        {v:"none", l: ic.steps.bariatric.none, mutex: true},
      ]
    },
    's13_prior_surgery': {
      title: ic.steps.priorSurgery.title,
      options: [{v:"No", l: ic.common.no},{v:"Yes", l: ic.common.yes}],
      textLabel: ic.steps.priorSurgery.detailLabel,
    },
    's14_current_glp1': {
      title: ic.steps.currentGlp1.title,
      subtitle: ic.steps.currentGlp1.subtitle,
    },
    's14a_glp1_med': {
      title: ic.steps.whichGlp1.title,
      options: getGLP1Medications(lang),
    },
    's14b_dose_known': {
      title: ic.steps.doseKnown.title,
    },
    's14c_specific_dose': {
      title: ic.steps.specificDose.title,
    },
    's14d_volume_concentration': {
      title: ic.steps.volumeConcentration.title,
    },
    's15_previously_taken_glp1': {
      title: ic.steps.previouslyTakenGlp1.title,
    },
    's15a_past_glp1_meds': {
      title: ic.steps.pastGlp1Meds.title,
      subtitle: ic.steps.pastGlp1Meds.subtitle,
      textLabel: ic.steps.pastGlp1Meds.textLabel,
      options: getPastGLP1Options(lang),
    },
    's16_pregnancy': {
      title: ic.steps.pregnancy.title,
      subtitle: ic.steps.pregnancy.subtitle,
      options: [
        {v:"Yes", l: ic.common.yes},
        {v:"No", l: ic.common.no},
        {v:"Not applicable", l: ic.steps.pregnancy.notApplicable}
      ]
    },
    's17_ethnicity': {
      title: ic.steps.ethnicity.title,
      subtitle: ic.steps.ethnicity.subtitle,
      options: [
        {v:"african_american", l: ic.steps.ethnicity.africanAmerican},
        {v:"caucasian", l: ic.steps.ethnicity.caucasian},
        {v:"hispanic_latin", l: ic.steps.ethnicity.hispanicLatin},
        {v:"native_american", l: ic.steps.ethnicity.nativeAmerican},
        {v:"pacific_islander", l: ic.steps.ethnicity.pacificIslander},
        {v:"asian", l: ic.steps.ethnicity.asian},
        {v:"other", l: ic.steps.ethnicity.other},
        {v:"prefer_not_to_say", l: ic.steps.ethnicity.preferNotToSay},
      ]
    },
    's18_recreational': {
      title: ic.steps.recreational.title,
      subtitle: ic.steps.recreational.subtitle,
      options: [{v:"No", l: ic.common.no},{v:"Yes", l: ic.common.yes},{v:"Prefer not to say", l: ic.common.preferNotToSay}],
      textLabel: ic.steps.recreational.detailLabel,
    },
    's19_current_conditions': {
      title: ic.steps.currentConditions.title,
      subtitle: ic.steps.currentConditions.subtitle,
      options: [
        {v:"active_cancer", l: ic.steps.currentConditions.activeCancer},
        {v:"active_gallbladder_disease", l: ic.steps.currentConditions.activeGallbladder},
        {v:"allergy_glp1", l: ic.steps.currentConditions.allergyGlp1},
        {v:"bowel_obstruction", l: ic.steps.currentConditions.bowelObstruction},
        {v:"current_diabetic_drugs", l: ic.steps.currentConditions.currentDiabeticDrugs},
        {v:"diabetes", l: ic.steps.currentConditions.diabetes},
        {v:"eating_disorder_12mo", l: ic.steps.currentConditions.eatingDisorder},
        {v:"endocrine_hormone_disorder", l: ic.steps.currentConditions.endocrineHormone},
        {v:"kidney_transplant", l: ic.steps.currentConditions.kidneyTransplant},
        {v:"medullary_thyroid_cancer", l: ic.steps.currentConditions.medullaryThyroid},
        {v:"multiple_endocrine_neoplasia", l: ic.steps.currentConditions.multipleEndocrine},
        {v:"pancreatitis", l: ic.steps.currentConditions.pancreatitis},
        {v:"severe_liver_disease", l: ic.steps.currentConditions.severeLiver},
        {v:"suicidal_thoughts_12mo", l: ic.steps.currentConditions.suicidalThoughts},
        {v:"none", l: ic.steps.currentConditions.none, mutex: true},
      ]
    },
    's20_weight_related': {
      title: ic.steps.weightRelated.title,
      options: [
        {v:"high_blood_pressure", l: ic.steps.weightRelated.highBloodPressure},
        {v:"high_cholesterol", l: ic.steps.weightRelated.highCholesterol},
        {v:"pre_diabetes", l: ic.steps.weightRelated.preDiabetes},
        {v:"metabolic_syndrome", l: ic.steps.weightRelated.metabolicSyndrome},
        {v:"osteoarthritis", l: ic.steps.weightRelated.osteoarthritis},
        {v:"cardiovascular_disease", l: ic.steps.weightRelated.cardiovascular},
        {v:"none", l: ic.steps.weightRelated.none, mutex: true},
      ]
    },
    's21_other_meds': {
      title: ic.steps.otherMeds.title,
      options: [{v:"No", l: ic.steps.otherMeds.no},{v:"Yes", l: ic.steps.otherMeds.yes}],
      textLabel: ic.steps.otherMeds.detailLabel,
    },
    's22_other_conditions': {
      title: ic.steps.otherConditions.title,
      textLabel: ic.steps.otherConditions.detailLabel,
    },
    's23_hypothyroidism': {
      title: ic.steps.hypothyroidism.title,
      subtitle: ic.steps.hypothyroidism.subtitle,
    },
    's24_intracranial': {
      title: ic.steps.intracranial.title,
      subtitle: ic.steps.intracranial.subtitle,
    },
    's25_hypersensitivity': {
      title: ic.steps.hypersensitivity.title,
      subtitle: ic.steps.hypersensitivity.subtitle,
    },
    's22a_nad_history': {
      title: ic.steps.nadHistory.title,
      subtitle: ic.steps.nadHistory.subtitle,
    },
    's22b_nad_sensitivity': {
      title: ic.steps.nadSensitivity.title,
    },
  };

  const translation = stepTranslations[step.id];
  if (!translation) return step;

  return {
    ...step,
    ...translation
  };
}

export function getGLP1Medications(lang) {
  const ic = getIntakeContent(lang);
  return [
    { v: "wegovy", l: ic.medications.wegovy },
    { v: "ozempic", l: ic.medications.ozempic },
    { v: "rybelsus", l: ic.medications.rybelsus },
    { v: "compounded_semaglutide", l: ic.medications.compounded_semaglutide },
    { v: "zepbound", l: ic.medications.zepbound },
    { v: "mounjaro", l: ic.medications.mounjaro },
    { v: "compounded_tirzepatide", l: ic.medications.compounded_tirzepatide },
    { v: "victoza", l: ic.medications.victoza },
    { v: "saxenda", l: ic.medications.saxenda },
    { v: "compounded_liraglutide", l: ic.medications.compounded_liraglutide },
    { v: "trulicity", l: ic.medications.trulicity },
    { v: "byetta", l: ic.medications.byetta },
  ];
}

// Keep for backwards compatibility
export const GLP1_MEDICATIONS = getGLP1Medications('en');

// Dynamic dose options keyed by selected medication.
export const GLP1_DOSES = {
  wegovy:                  ["0.25 mg","0.5 mg","1 mg","1.7 mg","2.4 mg","Other"],
  compounded_semaglutide:  ["0.25 mg","0.5 mg","1 mg","1.7 mg","2.4 mg","Other"],
  ozempic:                 ["0.25 mg","0.5 mg","1 mg","2 mg","Other"],
  rybelsus:                ["0.25 mg","0.5 mg","1 mg","2 mg","Other"],
  zepbound:                ["2.5 mg","5 mg","7.5 mg","10 mg","12.5 mg","15 mg","Other"],
  mounjaro:                ["2.5 mg","5 mg","7.5 mg","10 mg","12.5 mg","15 mg","Other"],
  compounded_tirzepatide:  ["2.5 mg","5 mg","7.5 mg","10 mg","12.5 mg","15 mg","Other"],
  victoza:                 ["0.6 mg","1.2 mg","1.8 mg","2.4 mg","3 mg","Other"],
  saxenda:                 ["0.6 mg","1.2 mg","1.8 mg","2.4 mg","3 mg","Other"],
  compounded_liraglutide:  ["0.6 mg","1.2 mg","1.8 mg","2.4 mg","3 mg","Other"],
  trulicity:               ["0.75 mg","1.5 mg","3 mg","4.5 mg","Other"],
  byetta:                  ["2 mg","Other"],
};

export function getPastGLP1Options(lang) {
  const ic = getIntakeContent(lang);
  return [
    { v: "semaglutide",  l: ic.pastMeds.semaglutide },
    { v: "tirzepatide",  l: ic.pastMeds.tirzepatide },
    { v: "liraglutide",  l: ic.pastMeds.liraglutide },
    { v: "trulicity",    l: ic.pastMeds.trulicity },
    { v: "exenatide",    l: ic.pastMeds.exenatide },
  ];
}

// Keep for backwards compatibility
export const PAST_GLP1_OPTIONS = getPastGLP1Options('en');

export const STEPS = [
  // ── 0. GLP-1 Intro (tirzepatide products/programs only) ──
  { id:"s00_glp1_intro", kind:"glp1_intro",
    skipIf:(a)=> !shouldAskGLP1Questions(a._intent) },

  // ── 1. State ──
  { id:"s01_state", kind:"state" },

  // ── 2. Name ──
  { id:"s02_name", kind:"name" },

  // ── 3. DOB ──
  { id:"s03_dob", kind:"dob" },

  // ── 4. Sex assigned at birth (auto-advance) ──
  { id:"s04_gender", kind:"radio",
    title:"What is your sex assigned at birth?",
    subtitle:"This helps us determine the safest treatment for you.",
    key:"gender",
    options:[{v:"Male"},{v:"Female"},{v:"Prefer not to say"}],
    autoAdvance:false },

  // ── 5. Height & weight + goal weight + live BMI ──
  { id:"s05_height_weight", kind:"hw" },

  // ── 6. Motivation (multi-select, required ≥1) ──
  { id:"s06_motivation", kind:"multi",
    title:"What inspires you to make this change?",
    subtitle:"Select all that apply.",
    key:"motivation",
    options:[
      {v:"confident",  l:"I want to feel more confident"},
      {v:"energy",     l:"I want to have more energy"},
      {v:"health",     l:"I want to improve my health"},
      {v:"active",     l:"I want to be more active"},
      {v:"feel_good",  l:"I want to feel good every day"},
    ]},

  // ── 7. Weight-loss goal ──
  { id:"s07_goal", kind:"radio",
    title:"What's your weight loss goal?",
    key:"weight_loss_goal",
    options:[
      {v:"1_15",   l:"1–15 lbs."},
      {v:"16_50",  l:"16–50 lbs."},
      {v:"51_plus",l:"51 or more lbs."},
      {v:"unsure", l:"I'm not sure yet — I just want to get started"},
    ],
    autoAdvance:false },

  // ── 8. Good news interstitial + BMI card ──
  { id:"s08_good_news", kind:"interstitial",
    eyebrow:"Good news!",
    title:"You may be eligible for treatment",
    body:"Based on your information, you may be eligible for GLP-1 treatment. Let's find the best option for your goals." },

  // ── 8a. Eligibility confirmation with image ──
  { id:"s08a_eligibility_confirmation", kind:"eligibility_confirmation" },

  // ── 9. Weight loss attempts (multi, required ≥1) ──
  { id:"s11_attempts", kind:"multi",
    title:"What ways have you tried to lose weight in the past?",
    subtitle:"Select all that apply.",
    key:"weight_loss_attempts",
    options:[
      {v:"counting_calories",     l:"Counting calories"},
      {v:"commercial_programs",   l:"Commercial weight loss programs or apps"},
      {v:"low_carb_keto",         l:"Low-carb, keto, or similar diets"},
      {v:"intermittent_fasting",  l:"Intermittent fasting"},
      {v:"exercise",              l:"Exercise"},
      {v:"other",                 l:"Other"},
    ]},

  // ── 10. Bariatric procedures (multi, mutex "None") ──
  { id:"s12_bariatric", kind:"multi",
    title:"Have you had any of the following bariatric procedures?",
    key:"bariatric_procedures",
    options:[
      {v:"lap_band",       l:"Lap band"},
      {v:"gastric_sleeve", l:"Gastric sleeve"},
      {v:"gastric_bypass", l:"Gastric bypass"},
      {v:"none",           l:"None of these", mutex:true},
    ]},

  // ── 11. Prior surgery / hospitalizations ──
  { id:"s13_prior_surgery", kind:"yesno_text",
    title:"Have you ever had any surgeries or hospitalizations?",
    key:"prior_surgery",
    options:[{v:"No"},{v:"Yes"}],
    textKey:"prior_surgery_detail",
    textLabel:"Please provide details",
    },

  // ── 12. Currently taking GLP-1 (only for GLP-1/tirzepatide intakes) ──
  { id:"s14_current_glp1", kind:"yesno",
    title:"Are you currently taking GLP-1 medication?",
    subtitle:"Such as Ozempic, Wegovy, Mounjaro, Zepbound, or compounded GLP-1.",
    key:"currently_taking_glp1",
    skipIf:(a)=> !shouldAskGLP1Questions(a._intent) },

  // ── 13. (s14a) Which GLP-1 medication ──
  { id:"s14a_glp1_med", kind:"radio_long",
    title:"Which medication are you taking?",
    key:"current_glp1_medication",
    options: GLP1_MEDICATIONS,
    skipIf:(a)=> !shouldAskGLP1Questions(a._intent) || a.currently_taking_glp1 !== "Yes" },

  // ── 14. (s14b) Dose known? ──
  { id:"s14b_dose_known", kind:"yesno",
    title:"Do you know your weekly dose in milligrams (mg)?",
    key:"current_glp1_dose_known",
    skipIf:(a)=> !shouldAskGLP1Questions(a._intent) || a.currently_taking_glp1 !== "Yes" },

  // ── 15. (s14c) Specific dose — DYNAMIC options ──
  { id:"s14c_specific_dose", kind:"dynamic_dose",
    title:"What dose are you taking?",
    key:"current_glp1_dose",
    dependsOn:"current_glp1_medication",
    skipIf:(a)=> !shouldAskGLP1Questions(a._intent) || a.currently_taking_glp1 !== "Yes" || a.current_glp1_dose_known !== "Yes" },

  // ── 16. (s14d) Volume + concentration fallback ──
  { id:"s14d_volume_concentration", kind:"yesno",
    title:"Do you know BOTH the volume (mL or units) you inject AND the concentration (mg/mL) of medication you inject?",
    key:"current_glp1_volume_concentration_known",
    skipIf:(a)=> !shouldAskGLP1Questions(a._intent) || a.currently_taking_glp1 !== "Yes" || a.current_glp1_dose_known !== "No" },

  // ── 17. (s15) Previously taken GLP-1 ──
  { id:"s15_previously_taken_glp1", kind:"yesno",
    title:"Have you previously taken GLP-1 medication?",
    key:"previously_taken_glp1",
    skipIf:(a)=> !shouldAskGLP1Questions(a._intent) },

  // ── 18+19. (s15a + s15b merged) Past GLP-1 meds (multi) + optional "other" textbox ──
  { id:"s15a_past_glp1_meds", kind:"multi_with_text",
    title:"Which medications have you taken in the past?",
    subtitle:"Select all that apply.",
    key:"past_glp1_medications",
    options: PAST_GLP1_OPTIONS,
    textKey:"past_glp1_other",
    textLabel:"Anything else not listed above? (optional)",
    skipIf:(a)=> !shouldAskGLP1Questions(a._intent) || a.previously_taken_glp1 !== "Yes" },

  // ── 20. (s16) Pregnancy — Female only ──
  { id:"s16_pregnancy", kind:"radio",
    title:"Are you pregnant or breastfeeding?",
    subtitle:"Or planning to become pregnant in the next 12 months.",
    key:"pregnant_breastfeeding",
    options:[{v:"Yes"},{v:"No"},{v:"Not applicable"}],
    autoAdvance:false,
    skipIf:(a)=> a.gender !== "Female" },

  // ── 21. (s17) Ethnicity (radio, single select) ──
  { id:"s17_ethnicity", kind:"radio",
    title:"What is your ethnicity?",
    subtitle:"We ask this to better tailor treatment options to you.",
    key:"ethnicity",
    options:[
      {v:"african_american",   l:"African American"},
      {v:"caucasian",          l:"Caucasian"},
      {v:"hispanic_latin",     l:"Hispanic or Latin"},
      {v:"native_american",    l:"Native American or Alaskan"},
      {v:"pacific_islander",   l:"Pacific Islander"},
      {v:"asian",              l:"Asian"},
      {v:"other",              l:"Other"},
      {v:"prefer_not_to_say",  l:"Prefer not to say"},
    ],
    autoAdvance:false },

  // ── 22. (s18) Recreational drugs ──
  { id:"s18_recreational", kind:"yesno_text",
    title:"Do you use recreational drugs?",
    subtitle:"To ensure your safety, please answer truthfully. Your information is confidential and reviewed only by your medical team.",
    key:"recreational_drugs",
    options:[{v:"No"},{v:"Yes"},{v:"Prefer not to say"}],
    textKey:"recreational_drugs_detail",
    textLabel:"Which ones? (Please specify)",
    },

  // ── 23. (s19) Current medical conditions (multi, mutex "None", required ≥1) ──
  { id:"s19_current_conditions", kind:"multi",
    title:"Are you currently being treated for any of the following conditions?",
    subtitle:"This information helps us ensure treatment is safe for you. Select all that apply.",
    key:"current_conditions",
    options:[
      {v:"active_cancer",                 l:"Active cancer or tumor (excluding non-melanoma skin cancer)"},
      {v:"active_gallbladder_disease",    l:"Active gallbladder disease"},
      {v:"allergy_glp1",                  l:"Allergy or hypersensitivity to GLP-1 medication"},
      {v:"bowel_obstruction",             l:"Bowel obstruction"},
      {v:"current_diabetic_drugs",        l:"Current use of diabetic drugs (sulfonylureas, meglitinides, insulin, or DPP-4 inhibitors)"},
      {v:"diabetes",                      l:"Diabetes (type 1, type 2, or unknown)"},
      {v:"eating_disorder_12mo",          l:"Eating disorder in the last 12 months"},
      {v:"endocrine_hormone_disorder",    l:"Endocrine or hormone disorder (e.g. pituitary, adrenal)"},
      {v:"kidney_transplant",             l:"Kidney transplant"},
      {v:"medullary_thyroid_cancer",      l:"Medullary thyroid cancer (personal or family)"},
      {v:"multiple_endocrine_neoplasia",  l:"Multiple endocrine neoplasia (MEN) (personal or family)"},
      {v:"pancreatitis",                  l:"Pancreatitis (history)"},
      {v:"severe_liver_disease",          l:"Severe liver disease (cirrhosis, fibrosis, or transplantation)"},
      {v:"suicidal_thoughts_12mo",        l:"Suicidal thoughts or suicide attempts in the last 12 months"},
      {v:"none",                          l:"None of the above", mutex:true},
    ]},

  // ── 24. (s20) Weight-related conditions (multi, mutex "None") ──
  { id:"s20_weight_related", kind:"multi",
    title:"Have you ever been diagnosed with any of the following health conditions related to weight?",
    key:"weight_related_conditions",
    options:[
      {v:"high_blood_pressure",    l:"High blood pressure (hypertension)"},
      {v:"high_cholesterol",       l:"High cholesterol (hyperlipidemia)"},
      {v:"pre_diabetes",           l:"Pre-diabetes"},
      {v:"metabolic_syndrome",     l:"Metabolic syndrome"},
      {v:"osteoarthritis",         l:"Osteoarthritis"},
      {v:"cardiovascular_disease", l:"Cardiovascular disease (ASCVD)"},
      {v:"none",                   l:"None of the above", mutex:true},
    ]},

  // ── 25. (s21) Other medications/supplements — Yes shows textarea, No auto-advance ──
  { id:"s21_other_meds", kind:"yesno_text",
    title:"Do you currently take any medications, herbal remedies, or supplements?",
    key:"taking_other_meds",
    options:[{v:"No", l:"No, I'm not taking any"},{v:"Yes", l:"Yes"}],
    textKey:"taking_other_meds_detail",
    textLabel:"Please list them",
    },

  // ── 26. (s22) Other medical conditions — Yes shows textarea, No auto-advance ──
  { id:"s22_other_conditions", kind:"yesno_text",
    key:"other_medical_conditions",
    textKey:"other_medical_conditions_detail",
    },

  // ── 26a-26b. NAD+-gated questions (skip unless intent calls for NAD+) ──
  { id:"s22a_nad_history", kind:"yesno",
    key:"nad_history",
    autoAdvance:false,
    skipIf:(a)=> !shouldAskNADQuestions(a._intent) },

  { id:"s22b_nad_sensitivity", kind:"yesno",
    key:"nad_sensitivity",
    autoAdvance:false,
    skipIf:(a)=> !shouldAskNADQuestions(a._intent) },

  // ── 27-29. Sermorelin-gated questions (skip unless intent calls for sermorelin) ──
  { id:"s23_hypothyroidism", kind:"yesno",
    title:"Do you have untreated hypothyroidism?",
    subtitle:"A diagnosis you have not yet started treatment for.",
    key:"untreated_hypothyroidism",
    autoAdvance:false,
    skipIf:(a)=> !shouldAskSermorelin(a._intent) },

  { id:"s24_intracranial", kind:"yesno",
    title:"Do you have a history of intracranial lesions?",
    subtitle:"Any tumors, cysts, or other lesions inside the skull.",
    key:"intracranial_lesions",
    autoAdvance:false,
    skipIf:(a)=> !shouldAskSermorelin(a._intent) },

  { id:"s25_hypersensitivity", kind:"yesno",
    title:"Do you have any hypersensitivity to sermorelin?",
    subtitle:"A known allergic or adverse reaction to sermorelin or growth hormone–releasing peptides.",
    key:"hypersensitivity_to_sermorelin",
    autoAdvance:false,
    skipIf:(a)=> !shouldAskSermorelin(a._intent) },

  // ── 30. Great choice interstitial ──
  { id:"s30_great_choice", kind:"great_choice" },
];

// Helper functions to determine which product-specific questions to show
export function shouldAskGLP1Questions(intent) {
  if (!intent || intent.kind === "generic") return true;
  if (intent.kind === "product" && intent.slug === "tirzepatide") return true;
  if (intent.kind === "program") {
    return ["tirzepatide-core", "metabolic-boost", "total-body-optimization", "advanced-transformation"].includes(intent.slug);
  }
  return false;
}

export function shouldAskNADQuestions(intent) {
  if (!intent || intent.kind === "generic") return true;
  if (intent.kind === "product" && intent.slug === "nad") return true;
  if (intent.kind === "program") {
    return ["metabolic-boost", "total-body-optimization", "advanced-transformation"].includes(intent.slug);
  }
  return false;
}

export function shouldAskSermorelin(intent) {
  if (!intent || intent.kind === "generic") return true;
  if (intent.kind === "product" && intent.slug === "sermorelin") return true;
  if (intent.kind === "program") {
    return ["total-body-optimization","advanced-transformation"].includes(intent.slug);
  }
  return false;
}

export const PROGRAMS_FOR_PICKER = [
  {slug:"tirzepatide-core", name:"Tirzepatide Core Program", desc:"Start controlling your appetite and begin structured weight loss."},
  {slug:"metabolic-boost", name:"Metabolic Boost Program", desc:"Weight loss + energy + mental clarity.", badge:"Most popular"},
  {slug:"total-body-optimization", name:"Total Body Optimization", desc:"Complete transformation: weight, recovery & antiaging."},
  {slug:"advanced-transformation", name:"Advanced Transformation", desc:"Maximum results for serious transformation."},
];
